import React, { useState, useRef } from "react";
import { m, useReducedMotion, useMotionValue, useSpring } from "framer-motion";
import { cn } from "../../lib/utils";
import { Building2, Crown, Gem, Rocket, ShieldCheck, Sparkles, TrendingUp, Zap } from "lucide-react";
import { CTA_GRADIENT, CTA_GRADIENT_HOVER } from "../../components/marketing/shared";


const EASE = [0.22, 1, 0.36, 1];
const REVEAL = {
  fade:  { hidden: { opacity: 0 },                     show: { opacity: 1,                transition: { duration: 0.45, ease: EASE } } },
  up:    { hidden: { opacity: 0, y: 28 },              show: { opacity: 1, y: 0,          transition: { duration: 0.6, ease: EASE } } },
  scale: { hidden: { opacity: 0, scale: 0.96, y: 22 }, show: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.65, ease: EASE } } },
  left:  { hidden: { opacity: 0, x: -16 },             show: { opacity: 1, x: 0,          transition: { duration: 0.5, ease: EASE } } },
  right: { hidden: { opacity: 0, x: 16 },              show: { opacity: 1, x: 0,          transition: { duration: 0.5, ease: EASE } } },
};

// Premium cursor-follow 3D tilt: the child (e.g. the phone mockup) softly tilts + lifts toward the
// cursor and springs back on leave. Spring-smoothed motion values (never jumps), a soft blue glow
// fades in on hover. Pure transform/opacity (GPU). Touch/reduced-motion → a plain, static wrapper.
function TiltCard({ children, className, maxTilt = 8, maxShift = 10, hoverScale = 1.03 }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const rx = useMotionValue(0), ry = useMotionValue(0), tx = useMotionValue(0), ty = useMotionValue(0), sc = useMotionValue(1);
  const [hovered, setHovered] = useState(false);
  const spring = { stiffness: 150, damping: 18, mass: 0.4 };
  const srx = useSpring(rx, spring), sry = useSpring(ry, spring);
  const stx = useSpring(tx, { stiffness: 150, damping: 20, mass: 0.4 }), sty = useSpring(ty, { stiffness: 150, damping: 20, mass: 0.4 });
  const ssc = useSpring(sc, { stiffness: 200, damping: 22 });

  if (reduce) return <div className={className}>{children}</div>;

  const onMove = (e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;   // -0.5 … 0.5
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 2 * maxTilt);            // horizontal cursor → rotateY  (±8°)
    rx.set(-py * 1.5 * maxTilt);         // vertical cursor   → rotateX  (±6°)
    tx.set(px * 2 * maxShift);           // ±10px
    ty.set(py * 2 * maxShift);
  };
  const onEnter = () => { setHovered(true); sc.set(hoverScale); };
  const onLeave = () => { setHovered(false); rx.set(0); ry.set(0); tx.set(0); ty.set(0); sc.set(1); };

  return (
    <m.div ref={ref} className={cn("relative", className)}
      onMouseMove={onMove} onMouseEnter={onEnter} onMouseLeave={onLeave}
      style={{ rotateX: srx, rotateY: sry, x: stx, y: sty, scale: ssc, transformPerspective: 1000, transformStyle: "preserve-3d", willChange: "transform" }}>
      <m.span aria-hidden="true" className="pointer-events-none absolute -inset-6 rounded-[3rem] bg-primary/25 blur-3xl"
        style={{ zIndex: -1 }} animate={{ opacity: hovered ? 1 : 0 }} transition={{ duration: 0.5, ease: EASE }} />
      {children}
    </m.div>
  );
}

// Single block fade/slide-in. `v` picks a REVEAL variant; `play="mount"` plays on mount (for
// above-the-fold blocks) instead of waiting for the viewport.
function Reveal({ as = "div", v = "up", amount = 0.2, play = "view", className, children, ...rest }) {
  const reduce = useReducedMotion();
  const M = m[as] || m.div;
  if (reduce) return <M className={className} {...rest}>{children}</M>;
  const trigger = play === "mount" ? { animate: "show" } : { whileInView: "show", viewport: { once: true, amount } };
  return <M className={className} variants={REVEAL[v]} initial="hidden" {...trigger} {...rest}>{children}</M>;
}

// Stagger parent — direct <StaggerItem> children (variant-driven) cascade in sequence.
function Stagger({ as = "div", stagger = 0.08, delay = 0.08, amount = 0.2, play = "view", className, children, ...rest }) {
  const reduce = useReducedMotion();
  const M = m[as] || m.div;
  if (reduce) return <M className={className} {...rest}>{children}</M>;
  const trigger = play === "mount" ? { animate: "show" } : { whileInView: "show", viewport: { once: true, amount } };
  return (
    <M className={className} variants={{ hidden: {}, show: { transition: { staggerChildren: stagger, delayChildren: delay } } }}
       initial="hidden" {...trigger} {...rest}>{children}</M>
  );
}

function StaggerItem({ as = "div", v = "up", className, children, ...rest }) {
  const reduce = useReducedMotion();
  const M = m[as] || m.div;
  if (reduce) return <M className={className} {...rest}>{children}</M>;
  return <M className={className} variants={REVEAL[v]} {...rest}>{children}</M>;
}

const PRICING_ICONS = { zap: Zap, crown: Crown, shield: ShieldCheck };

// Distinct premium glyph per plan (never the same icon twice), keyed on the PLAN ID: a display
// name is CMS copy and has already changed once (Starter -> Essentials, Premium -> Pro), which
// silently emptied a name-keyed lookup and rendered every card with the fallback icon.
//
// The per-plan TAGLINE map is retired: the CMS `audience` line on each plan card says who the plan
// is for, in the platform team's own words, and two sources for one sentence is how a card came to
// show a tagline that contradicted its own bullets.
const PLAN_ICONS = {
  plan_free: Sparkles,
  plan_starter: Rocket,
  plan_growth: TrendingUp,
  plan_premium: Gem,
  plan_enterprise: Building2,
};

// Per-plan pricing CTA recipes — blue-outline default · gradient for the featured plan · orange-outline for Enterprise.
// DELTA-7: scoped transition list (transform + the paint properties the variants actually
// change) — `transition-all` watched every property including layout.
const PRICE_BTN_BASE = "group/btn inline-flex items-center justify-center gap-2 w-full h-[48px] rounded-full text-[15px] font-semibold whitespace-nowrap transition-[transform,background-color,background-image,border-color,box-shadow,color] duration-200 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2";
const PRICE_BTN = {
  primary: `text-white ${CTA_GRADIENT} shadow-[0_10px_24px_rgba(29,78,216,0.22)] ${CTA_GRADIENT_HOVER} hover:-translate-y-px hover:shadow-[0_14px_30px_rgba(29,78,216,0.30)]`,
  blueOutline: "bg-surface text-primary border border-primary/40 hover:bg-primary/[0.06] hover:border-primary hover:-translate-y-px",
  orangeOutline: "bg-surface text-orange-dark dark:text-orangeWarm-400 border border-orange-border/70 hover:bg-orange-soft hover:border-orange hover:-translate-y-px",
};

// WP-05: Modules and Pricing are real ROUTES (`to`) so they crawl, middle-click and share;
// Why/FAQ remain homepage sections (this nav renders only on "/"), the product preview is a
// scripted on-page scroll. If it changes the URL, it must render as a Link.
// PGC-7 (2026-08-18): "Live Dashboard" overstated the target — the visual self-labels
// "Illustrative product preview · synthetic sample data", so the nav item now says so.
const NAV_LINKS = [
  { to: "/modules", label: "Modules" },
  { href: "#dashboard-showcase", label: "Product preview", dashboard: true },
  { href: "#why", label: "Why us" },
  { to: "/pricing", label: "Pricing" },
  { href: "#faq", label: "FAQ" },
];

// Audience cards reuse the live /solutions/* routes (testids preserved). [icon, label, route-slug, subtitle, bestFitTags]
// Aira AI showcase lives in components/AiraAISection (guided public sample + live-assistant handoff).

// Fallback FAQ shown before /public/marketing resolves (or if it fails). Kept in sync with the
// editable source of truth — core/marketing.py DEFAULTS["faq"] — which the platform admin can edit.

export { EASE, REVEAL, TiltCard, Reveal, Stagger, StaggerItem, PRICING_ICONS, PLAN_ICONS, PRICE_BTN_BASE, PRICE_BTN, NAV_LINKS };
